import { MapPin } from 'lucide-react';
import { Link } from 'react-router';
import { ArrowIcon } from '@/components/ui/Button';
import { Photo } from '@/components/ui/Photo';
import { Reveal } from '@/components/ui/Reveal';
import { SectionIntro } from '@/components/ui/SectionIntro';
import type { PhotoName } from '@/features/site/media';
import { useSite } from '@/features/site/SiteProvider';

// Representative imagery per division (not photographs of the company's own facilities).
const DIVISION_MEDIA: { photo: PhotoName; to: string }[] = [
  { photo: 'windingLine', to: '/apparel' },
  { photo: 'cricketBallSeam', to: '/sports-goods' },
];

/** Section F — manufacturing locations, from the CMS "about.divisions" content. */
export function Locations() {
  const { divisions } = useSite().content.about;
  if (divisions.length === 0) return null;

  return (
    <section aria-labelledby="locations-title" className="section-y bg-white">
      <div className="container-page">
        <SectionIntro
          id="locations-title"
          eyebrow="Manufacturing locations"
          title="Two manufacturing cities, one export partner."
          intro="Apparel and sports goods are produced in Pakistan's specialised manufacturing centres and consolidated for shipment."
        />

        <div className="mt-14 grid gap-10 md:grid-cols-2 md:gap-8">
          {divisions.map((d, i) => {
            const media = DIVISION_MEDIA[i];
            return (
              <Reveal as="article" key={d.title} delay={i * 100} className="group">
                {media && (
                  <figure>
                    <div className="aspect-[16/10] overflow-hidden rounded-[8px] bg-surface">
                      <Photo name={media.photo} zoom sizes="(min-width: 768px) 50vw, 100vw" />
                    </div>
                    <figcaption className="mt-2 text-[12px] text-muted">Representative image</figcaption>
                  </figure>
                )}
                <p className="mt-5 flex items-center gap-2 text-[13px] font-semibold tracking-[0.12em] text-accent-dark uppercase">
                  <MapPin className="h-4 w-4" aria-hidden />
                  {d.location}
                </p>
                <h3 className="mt-2 font-display text-[26px] font-bold tracking-tight text-primary">{d.title}</h3>
                <p className="mt-3 max-w-[540px] text-[15.5px] leading-[1.7] text-muted">{d.description}</p>
                {d.products.length > 0 && (
                  <ul className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-[14px] text-ink-soft">
                    {d.products.map((p) => (
                      <li key={p} className="flex items-center gap-2">
                        <span aria-hidden className="h-1 w-1 rounded-full bg-accent" />
                        {p}
                      </li>
                    ))}
                  </ul>
                )}
                {media && (
                  <Link to={media.to} className="mt-6 inline-flex items-center gap-2 text-[14px] font-semibold text-primary underline-offset-4 hover:underline">
                    View products <ArrowIcon />
                  </Link>
                )}
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
